import { baseLanguageCode } from "./language";

function formatRomanianWeightOrVolume(value: string): string {
  return value
    .replace(/\b(?:kg|ml|g|l)\b/gi, (unit) => unit.toLocaleLowerCase("ro"))
    .replace(/\b(?:buc(?:ăți)?|pcs?|pieces?)\.?/giu, "buc.")
    .replace(/\b(?:role|rola|rolls?)\.?/giu, "rol.")
    .replace(/\b(l|kg|ml|g)\.(?=\s|$)/gi, "$1");
}

function formatRussianWeightOrVolume(value: string): string {
  return value
    .replace(/\bkg\b/gi, "кг")
    .replace(/\bml\b/gi, "мл")
    .replace(/\bg\b/gi, "г")
    .replace(/\bl\b/gi, "л")
    .replace(/\b(?:buc(?:ăți)?|pcs?|pieces?)\.?/giu, "шт.")
    .replace(/\b(?:role|rola|rolls?)\.?/giu, "рул.")
    .replace(/(кг|мл|г|л)\.(?=\s|$)/g, "$1");
}

function formatUkrainianWeightOrVolume(value: string): string {
  return value
    .replace(/\bkg\b/gi, "кг")
    .replace(/\bml\b/gi, "мл")
    .replace(/\bg\b/gi, "г")
    .replace(/\bl\b/gi, "л")
    .replace(/\b(?:buc(?:ăți)?|pcs?|pieces?)\.?/giu, "шт.")
    .replace(/\b(?:role|rola|rolls?)\.?/giu, "рул.")
    .replace(/(кг|мл|г|л)\.(?=\s|$)/g, "$1");
}

export const APP_LOCALE_DEFINITIONS = {
  ru: {
    tag: "ru-RU",
    formatWeightOrVolume: formatRussianWeightOrVolume,
  },
  ro: {
    tag: "ro-RO",
    formatWeightOrVolume: formatRomanianWeightOrVolume,
  },
  uk: {
    tag: "uk-UA",
    formatWeightOrVolume: formatUkrainianWeightOrVolume,
  },
} as const;

export type AppLocale = keyof typeof APP_LOCALE_DEFINITIONS;
export type AppLocaleTag =
  (typeof APP_LOCALE_DEFINITIONS)[AppLocale]["tag"];
export type Localized<T> = Readonly<Record<AppLocale, T>>;

export const APP_LOCALES = Object.freeze(
  Object.keys(APP_LOCALE_DEFINITIONS) as AppLocale[],
);

export const DEFAULT_APP_LOCALE: AppLocale = "ru";

export function localized<T>(
  locale: AppLocale,
  values: Localized<T>,
): T {
  return values[locale];
}

export function nextAppLocale(locale: AppLocale): AppLocale {
  const index = APP_LOCALES.indexOf(locale);
  return APP_LOCALES[(index + 1) % APP_LOCALES.length] ?? DEFAULT_APP_LOCALE;
}

const ROMANIAN_PLURAL_RULES = new Intl.PluralRules("ro-RO");

const ROMANIAN_COUNT_FORMS = {
  product: { one: "produs", few: "produse", other: "de produse" },
  offer: { one: "ofertă", few: "oferte", other: "de oferte" },
  catalog: { one: "catalog", few: "cataloage", other: "de cataloage" },
} as const;

export type RomanianCountNoun = keyof typeof ROMANIAN_COUNT_FORMS;

export function romanianCountLabel(
  value: number,
  noun: RomanianCountNoun,
): string {
  const forms = ROMANIAN_COUNT_FORMS[noun];
  switch (ROMANIAN_PLURAL_RULES.select(Math.abs(value))) {
    case "one":
      return forms.one;
    case "few":
      return forms.few;
    default:
      return forms.other;
  }
}

export function normalizeAppLocale(value: unknown): AppLocale | null {
  return typeof value === "string" &&
    Object.prototype.hasOwnProperty.call(APP_LOCALE_DEFINITIONS, value)
    ? (value as AppLocale)
    : null;
}

export function appLocaleFromLanguageCode(value: unknown): AppLocale | null {
  return normalizeAppLocale(baseLanguageCode(value));
}

export function detectAppLocale(
  telegramLanguageCode: unknown,
  browserLanguages: readonly string[] = [],
): AppLocale {
  const telegramLocale = appLocaleFromLanguageCode(telegramLanguageCode);
  if (telegramLocale) {
    return telegramLocale;
  }
  for (const language of browserLanguages) {
    const locale = appLocaleFromLanguageCode(language);
    if (locale) {
      return locale;
    }
  }
  return DEFAULT_APP_LOCALE;
}

export function localeTag(locale: AppLocale): AppLocaleTag {
  return APP_LOCALE_DEFINITIONS[locale].tag;
}

export function formatWeightOrVolume(
  value: string,
  locale: AppLocale,
): string {
  const normalized = value.trim().replace(/(\d)\.(\d)/g, "$1,$2");
  return APP_LOCALE_DEFINITIONS[locale].formatWeightOrVolume(normalized);
}
